import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AddLinkOutlinedIcon from "@mui/icons-material/AddLinkOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SearchIcon from "@mui/icons-material/Search";
import { commonRunQuery } from "../../util/API/loans";

const colors = {
  navy: "#003B5C",
  blue: "#0A93D2",
  yellow: "#FAC800",
  green: "#2E9D74",
  text: "#263238",
  muted: "#6B7280",
  border: "#E3E9EE",
  surface: "#F7F9FB",
};

function UserAssociations({ selectedUser, selectedUserData }) {
  const [associations, setAssociations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showAddAssociation, setShowAddAssociation] = useState(false);
  const [accountSearch, setAccountSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const getUserAssociationList = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");

    try {
      const response = await commonRunQuery(306, selectedUser);
      const responseData = response?.data ?? response;
      const record = responseData?.body?.record;

      if (responseData?.header?.callStatus === "1") {
        const associationList = Array.isArray(record)
          ? record
          : record && typeof record === "object"
            ? [record]
            : [];

        setAssociations(associationList);
      } else {
        setAssociations([]);
        setLoadError("Unable to load user associations.");
      }
    } catch (error) {
      console.error("Unable to load user associations:", error);
      setAssociations([]);
      setLoadError("Unable to load user associations. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedUser]);

  useEffect(() => {
    getUserAssociationList();
  }, [getUserAssociationList]);

  useEffect(() => {
    const lastAvailablePage = Math.max(
      0,
      Math.ceil(associations.length / rowsPerPage) - 1,
    );
    setPage((currentPage) => Math.min(currentPage, lastAvailablePage));
  }, [associations.length, rowsPerPage]);

  const selectedProfile =
    selectedUserData?.User ?? selectedUserData?.Vuser ?? selectedUserData ?? {};
  const selectedUserName = [
    selectedProfile?.firstName,
    selectedProfile?.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  const getInitials = (user) =>
    `${user?.firstName?.charAt(0) ?? ""}${user?.lastName?.charAt(0) ?? ""}`.toUpperCase() ||
    "UA";

  const getAccountName = (user) => {
    if (user?.businessname?.trim()) return user.businessname;
    return [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Unnamed account";
  };

  const getBrokerName = (user) => {
    if (user?.brokerBusinessName?.trim()) return user.brokerBusinessName;
    return (
      [user?.brokerFirstName, user?.brokerLastName].filter(Boolean).join(" ") ||
      "Not available"
    );
  };

  const paginatedAssociations = associations.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box
      sx={{
        minHeight: 0,
        p: { xs: 2, md: 3 },
        bgcolor: "#FFFFFF",
        color: colors.text,
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              display: "grid",
              placeItems: "center",
              borderRadius: 2.5,
              color: colors.navy,
              bgcolor: "#EAF5FA",
            }}
          >
            <AccountTreeOutlinedIcon />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "Nunito", fontSize: 20, fontWeight: 700 }}>
              User Associations
            </Typography>
            <Typography sx={{ fontFamily: "Nunito", fontSize: 13, color: colors.muted }}>
              Manage accounts connected to
              {selectedUserName ? ` ${selectedUserName}` : " this user"}.
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="contained"
          startIcon={<AddLinkOutlinedIcon />}
          onClick={() => setShowAddAssociation((current) => !current)}
          sx={{
            minWidth: 174,
            py: 1.15,
            borderRadius: 2,
            bgcolor: colors.blue,
            fontFamily: "Nunito",
            fontWeight: 700,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { bgcolor: "#087FB5", boxShadow: "none" },
          }}
        >
          Add Association
        </Button>
      </Stack>

      {showAddAssociation && (
        <Paper
          variant="outlined"
          sx={{
            mt: 3,
            p: { xs: 2, md: 2.5 },
            borderRadius: 3,
            borderColor: "#CFE1EA",
            bgcolor: "#F8FCFE",
            boxShadow: "none",
            width: "100%",
            height: "auto !important",
            minHeight: "0 !important",
            maxHeight: "220px !important",
            flex: "0 0 auto",
            alignSelf: "flex-start",
            boxSizing: "border-box",
            overflow: "visible",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography sx={{ fontFamily: "Nunito", fontSize: 16, fontWeight: 700 }}>
                Find an account to associate
              </Typography>
              <Typography
                sx={{ mt: 0.25, fontFamily: "Nunito", fontSize: 13, color: colors.muted }}
              >
                Search by customer name, user ID, email, or account number.
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label="Close add association panel"
              onClick={() => {
                setShowAddAssociation(false);
                setAccountSearch("");
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              value={accountSearch}
              onChange={(event) => setAccountSearch(event.target.value)}
              placeholder="Search accounts"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#78909C" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: "#FFFFFF",
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
              }}
            />
            <Button
              variant="contained"
              disabled={!accountSearch.trim()}
              sx={{
                minWidth: 112,
                borderRadius: 2,
                bgcolor: colors.navy,
                fontFamily: "Nunito",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { bgcolor: "#002F49", boxShadow: "none" },
              }}
            >
              Search
            </Button>
          </Stack>
        </Paper>
      )}

      <Divider sx={{ my: 3, borderColor: colors.border }} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontFamily: "Nunito", fontSize: 16, fontWeight: 700 }}>
            Currently Associated Accounts
          </Typography>
          <Typography sx={{ fontFamily: "Nunito", fontSize: 13, color: colors.muted }}>
            Accounts with an active relationship to this user.
          </Typography>
        </Box>
        {!isLoading && (
          <Chip
            label={`${associations.length} ${associations.length === 1 ? "account" : "accounts"}`}
            size="small"
            sx={{
              bgcolor: "#EEF6F2",
              color: colors.green,
              fontFamily: "Nunito",
              fontWeight: 700,
            }}
          />
        )}
      </Stack>

      {isLoading ? (
        <Box
          sx={{
            minHeight: 260,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <CircularProgress size={34} sx={{ color: colors.blue }} />
          <Typography sx={{ fontFamily: "Nunito", fontSize: 13, color: colors.muted }}>
            Loading associations...
          </Typography>
        </Box>
      ) : loadError ? (
        <Paper
          variant="outlined"
          sx={{
            minHeight: 220,
            display: "grid",
            placeItems: "center",
            p: 3,
            borderRadius: 3,
            borderColor: "#F0CDD0",
            bgcolor: "#FFF9F9",
            boxShadow: "none",
          }}
        >
          <Stack alignItems="center" spacing={1.5}>
            <Typography sx={{ fontFamily: "Nunito", color: "#B4232C", fontWeight: 700 }}>
              {loadError}
            </Typography>
            <Button
              variant="outlined"
              onClick={getUserAssociationList}
              sx={{ textTransform: "none", fontFamily: "Nunito" }}
            >
              Try again
            </Button>
          </Stack>
        </Paper>
      ) : associations.length > 0 ? (
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            borderColor: colors.border,
            boxShadow: "0 8px 24px rgba(31, 55, 75, 0.05)",
            overflow: "hidden",
            width: "100%",
            minWidth: 0,
            height: { xs: "300px !important", sm: "320px !important" },
            maxHeight: { xs: "300px !important", sm: "320px !important" },
            minHeight: "0 !important",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            flex: "0 0 auto",
            alignSelf: "flex-start",
            bgcolor: "#FFFFFF",
          }}
        >
          <TableContainer
            sx={{
              width: "100%",
              flex: "1 1 auto",
              minHeight: 0,
              maxHeight: "none",
              overflowX: "auto",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              bgcolor: "#FFFFFF",
            }}
          >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 680,
              tableLayout: "auto",
              bgcolor: "#FFFFFF",
              "& .MuiTableBody-root .MuiTableCell-root": {
                bgcolor: "#FFFFFF",
              },
              "& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root": {
                bgcolor: "#FBFDFE",
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: "#FFFFFF" }}>
                {["Associated account", "User ID", "Broker / organization", "Status", "Action"].map(
                  (heading) => (
                    <TableCell
                      key={heading}
                      align={heading === "Action" ? "right" : "left"}
                      sx={{
                        py: 1.5,
                        borderColor: colors.border,
                        color: colors.muted,
                        fontFamily: "Nunito",
                        fontSize: 12,
                        fontWeight: 800,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        bgcolor: "#FFFFFF",
                      }}
                    >
                      {heading}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAssociations.map((association, index) => {
                const user =
                  association?.vuser ?? association?.Vuser ?? association?.user ?? {};

                return (
                  <TableRow
                    key={user?.id ?? page * rowsPerPage + index}
                    hover
                    sx={{
                      bgcolor: "#FFFFFF",
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": { bgcolor: "#FBFDFE" },
                      "&:hover td": { bgcolor: "#FBFDFE" },
                    }}
                  >
                    <TableCell sx={{ py: 1.25, borderColor: colors.border }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{
                            width: 38,
                            height: 38,
                            bgcolor: "#EAF5FA",
                            color: colors.navy,
                            fontFamily: "Nunito",
                            fontSize: 14,
                            fontWeight: 800,
                          }}
                        >
                          {getInitials(user)}
                        </Avatar>
                        <Box>
                          <Typography
                            sx={{
                              fontFamily: "Nunito",
                              fontSize: 14,
                              fontWeight: 700,
                              color: colors.text,
                            }}
                          >
                            {getAccountName(user)}
                          </Typography>
                          {user?.businessname && (user?.firstName || user?.lastName) && (
                            <Typography
                              sx={{ fontFamily: "Nunito", fontSize: 12, color: colors.muted }}
                            >
                              {[user?.firstName, user?.lastName].filter(Boolean).join(" ")}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.border }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <PersonOutlineIcon sx={{ fontSize: 17, color: "#78909C" }} />
                        <Typography sx={{ fontFamily: "Nunito", fontSize: 14, fontWeight: 600 }}>
                          {user?.id || "—"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.border }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <BusinessOutlinedIcon sx={{ fontSize: 17, color: "#78909C" }} />
                        <Typography sx={{ fontFamily: "Nunito", fontSize: 14 }}>
                          {getBrokerName(user)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ borderColor: colors.border }}>
                      <Chip
                        label="Active"
                        size="small"
                        sx={{
                          bgcolor: "#EAF7F1",
                          color: colors.green,
                          fontFamily: "Nunito",
                          fontSize: 12,
                          fontWeight: 800,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ borderColor: colors.border }}>
                      <Tooltip title="Remove association">
                        <IconButton
                          aria-label={`Remove association with ${getAccountName(user)}`}
                          sx={{
                            color: "#C93C45",
                            border: "1px solid #F0CDD0",
                            borderRadius: 2,
                            "&:hover": { bgcolor: "#FFF1F2" },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={associations.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Rows per page"
            sx={{
              borderTop: `1px solid ${colors.border}`,
              bgcolor: "#FFFFFF",
              color: colors.muted,
              fontFamily: "Nunito",
              mt: "auto",
              flexShrink: 0,
              "& .MuiTablePagination-toolbar": {
                minHeight: 52,
                px: { xs: 1, sm: 2 },
                flexWrap: "wrap",
              },
              "& .MuiTablePagination-selectLabel": {
                display: { xs: "none", sm: "block" },
              },
              "& .MuiTablePagination-displayedRows": {
                my: 0,
                fontFamily: "Nunito",
                fontSize: 12,
              },
              "& .MuiTablePagination-actions": {
                ml: { xs: 0.5, sm: 2 },
              },
            }}
          />
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            minHeight: 250,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 3,
            borderRadius: 3,
            borderColor: colors.border,
            borderStyle: "dashed",
            bgcolor: "#FBFCFD",
            boxShadow: "none",
          }}
        >
          <Box
            sx={{
              width: 54,
              height: 54,
              display: "grid",
              placeItems: "center",
              borderRadius: "50%",
              bgcolor: "#EAF5FA",
              color: colors.navy,
            }}
          >
            <AccountTreeOutlinedIcon />
          </Box>
          <Typography sx={{ mt: 1.5, fontFamily: "Nunito", fontSize: 16, fontWeight: 700 }}>
            No associated accounts
          </Typography>
          <Typography
            align="center"
            sx={{ mt: 0.5, maxWidth: 390, fontFamily: "Nunito", fontSize: 13, color: colors.muted }}
          >
            This user does not have any active account associations yet. Add an association to
            connect another account.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddLinkOutlinedIcon />}
            onClick={() => setShowAddAssociation(true)}
            sx={{
              mt: 2,
              borderRadius: 2,
              borderColor: colors.navy,
              color: colors.navy,
              fontFamily: "Nunito",
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            Add first association
          </Button>
        </Paper>
      )}

      <Box sx={{ mt: 2, px: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: colors.yellow }} />
        <Typography sx={{ fontFamily: "Nunito", fontSize: 12, color: colors.muted }}>
          Removing an association will deactivate the relationship without deleting either user
          account.
        </Typography>
      </Box>
    </Box>
  );
}

export default UserAssociations;
